import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import DGFilter, { useDGFilter, DG_LABELS } from "@/components/DGFilter";
import {
  Trophy, Plus, ChevronRight, X, Building2, Euro, Calendar, Users,
  CheckCircle, FileSignature, Rocket, Play, ChevronDown, ChevronUp,
  ClipboardList, Trash2, Edit2, Save, RefreshCw, ArrowLeft, Download, Loader2
} from "lucide-react";
import { HelpTip } from "@/components/HelpTip";

function SyncFromVsmButton({ onSynced }) {
  const [syncing, setSyncing] = React.useState(false);
  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/adjudicaciones/sync-from-vsm`);
      toast.success(`Sync: ${data.inserted} nuevas · ${data.updated} actualizadas (${data.vsm_rows} en VSM)`);
      onSynced?.();
    } catch (e) {
      toast.error(`Error sync: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSyncing(false);
    }
  };
  return (
    <button
      onClick={sync}
      disabled={syncing}
      className="flex items-center gap-2 border border-[#007934] text-[#007934] hover:bg-[#E8F5E9] px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50"
      data-testid="sync-from-vsm-btn"
      title="Sincroniza desde Contratación VSM las ofertas con fase ADJUDICADA o FIRMADA. Idempotente — corre también automáticamente cada día a las 03:15 UTC.">
      {syncing ? <Loader2 size={15} className="animate-spin"/> : <Download size={15}/>}
      Sincronizar desde VSM
    </button>
  );
}

const STATUS_CONFIG = {
  primero:     { label: "1º Clasificados", color: "bg-yellow-100 text-yellow-800 border-yellow-200", dot: "bg-yellow-400" },
  para_firma:  { label: "Para Firma",      color: "bg-blue-100 text-blue-800 border-blue-200",   dot: "bg-blue-500" },
  firmado:     { label: "Firmado",         color: "bg-purple-100 text-purple-800 border-purple-200", dot: "bg-purple-500" },
  arrancado:   { label: "Arrancado",       color: "bg-green-100 text-green-800 border-green-200", dot: "bg-green-500" },
  en_ejecucion:{ label: "En Ejecución",   color: "bg-teal-100 text-teal-800 border-teal-200",   dot: "bg-teal-500" },
};

const STATUSES = Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, ...v }));

// V07 (revisión 2026-10-11) · mismas etiquetas/colores del traspaso (R12)
// que ya usa Checklists.jsx — solo para mostrar el estado de implantación
// vinculado a cada contrato, sin duplicar la lógica de transiciones (eso
// solo vive en Checklists).
const HANDOFF_LABELS = {
  borrador: "Borrador", pendiente_aceptacion: "Pendiente de aceptación", aceptado: "Aceptado",
  en_implantacion: "En implantación", pendiente_puesta_servicio: "Pendiente de puesta en servicio",
  puesta_servicio_validada: "Puesta en servicio validada", cerrado: "Cerrado",
  devuelto_comercial: "Devuelto a Comercial", cancelado: "Cancelado",
};
const HANDOFF_COLOR = {
  borrador: "bg-gray-100 text-gray-600", pendiente_aceptacion: "bg-amber-100 text-amber-700",
  aceptado: "bg-blue-100 text-blue-700", en_implantacion: "bg-indigo-100 text-indigo-700",
  pendiente_puesta_servicio: "bg-amber-100 text-amber-700", puesta_servicio_validada: "bg-teal-100 text-teal-700",
  cerrado: "bg-green-100 text-green-700", devuelto_comercial: "bg-red-100 text-red-700",
  cancelado: "bg-gray-200 text-gray-500",
};
const SERVICIOS_TIC = ["Plataforma TIC", "GIS", "API Movilidad", "RFID/IoT", "App Ciudadano", "Gestión Flota", "Telefonía Móvil", "Fibra Óptica", "Fichaje", "Desarrollo a Medida"];

// ── Fase template default ─────────────────────────────────
const DEFAULT_FASES = [
  { id: null, mes: 1, titulo: "Toma de datos y planificación", descripcion: "Reunión kick-off, levantamiento de requisitos, acceso a sistemas", responsable: "", estado: "pendiente", tareas: [] },
  { id: null, mes: 2, titulo: "Despliegue infraestructura y plataforma core", descripcion: "Setup servidores, instalación plataforma base, configuración inicial", responsable: "", estado: "pendiente", tareas: [] },
  { id: null, mes: 3, titulo: "Integraciones y configuración", descripcion: "Integraciones con sistemas existentes, carga de datos, formación", responsable: "", estado: "pendiente", tareas: [] },
  { id: null, mes: 4, titulo: "Pruebas, Go-Live y soporte", descripcion: "UAT, corrección de incidencias, puesta en marcha y soporte inicial", responsable: "", estado: "pendiente", tareas: [] },
];

// ── Modal crear adjudicación ──────────────────────────────
function CreateModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", municipality: "", status: "primero", contrato_importe: "", fecha_firma_prevista: "", responsible_comercial: "", responsible_tech: "", servicios: [], notas: "", duracion_contrato_meses: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleServicio = (s) => set("servicios", form.servicios.includes(s) ? form.servicios.filter(x => x !== s) : [...form.servicios, s]);

  const save = async () => {
    if (!form.title.trim() || !form.municipality.trim()) return toast.error("Título y municipio son obligatorios");
    setSaving(true);
    try {
      await axios.post(`${API}/adjudicaciones`, { ...form, contrato_importe: form.contrato_importe ? parseFloat(form.contrato_importe) : null, duracion_contrato_meses: form.duracion_contrato_meses ? parseInt(form.duracion_contrato_meses) : null });
      toast.success("Adjudicación creada");
      onSaved();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-bold text-gray-900 text-lg">Nueva Adjudicación</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Título / Nombre contrato *</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                value={form.title} onChange={e => set("title", e.target.value)} placeholder="Ej: Servicio RSU y Limpieza Vial — Zaragoza" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Municipio *</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                value={form.municipality} onChange={e => set("municipality", e.target.value)} placeholder="Nombre del municipio" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Estado</label>
              <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                value={form.status} onChange={e => set("status", e.target.value)}>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Importe contrato (€)</label>
              <input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                value={form.contrato_importe} onChange={e => set("contrato_importe", e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Duración contrato (meses)</label>
              <input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                value={form.duracion_contrato_meses} onChange={e => set("duracion_contrato_meses", e.target.value)} placeholder="Ej: 48" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Fecha firma prevista</label>
              <input type="date" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                value={form.fecha_firma_prevista} onChange={e => set("fecha_firma_prevista", e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Responsable Comercial</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                value={form.responsible_comercial} onChange={e => set("responsible_comercial", e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Responsable Técnico</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                value={form.responsible_tech} onChange={e => set("responsible_tech", e.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">Servicios TIC adjudicados</label>
            <div className="flex flex-wrap gap-1.5">
              {SERVICIOS_TIC.map(s => (
                <button key={s} onClick={() => toggleServicio(s)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-all ${form.servicios.includes(s) ? "bg-[#007934] text-white border-[#007934]" : "border-gray-200 text-gray-500 hover:border-[#007934]"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Notas</label>
            <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none"
              value={form.notas} onChange={e => set("notas", e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-gray-200 text-gray-600 hover:bg-gray-50">Cancelar</button>
          <button onClick={save} disabled={saving}
            className="px-5 py-2 rounded-lg text-sm bg-[#007934] text-white font-semibold hover:bg-[#006528] disabled:opacity-50 flex items-center gap-2">
            {saving && <RefreshCw size={12} className="animate-spin" />} Crear adjudicación
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Plan de Arranque detail ───────────────────────────────
function PlanArranqueView({ adj, onBack, onRefresh }) {
  const [plan, setPlan] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ jefe_servicio: "", fases: DEFAULT_FASES, recursos_personas: [], recursos_licencias: [], recursos_infra: [], notas: "" });

  useEffect(() => {
    axios.get(`${API}/adjudicaciones/${adj.id}/arranque`).then(r => {
      const data = r.data || {};
      if (data.fases && data.fases.length > 0) {
        setPlan(data);
        setForm(data);
      } else {
        setForm(f => ({ ...f, jefe_servicio: adj.responsible_tech || "" }));
      }
    });
  }, [adj.id, adj.responsible_tech]);

  const save = async () => {
    setSaving(true);
    try {
      const resp = await axios.put(`${API}/adjudicaciones/${adj.id}/arranque`, form);
      setPlan(resp.data);
      setEditing(false);
      toast.success("Plan guardado");
      onRefresh();
    } catch { toast.error("Error al guardar el plan"); }
    finally { setSaving(false); }
  };

  const vistoBueno = async () => {
    if (!window.confirm("¿Confirmar Visto Bueno del Plan de Arranque?")) return;
    try {
      await axios.post(`${API}/adjudicaciones/${adj.id}/arranque/visto-bueno`, { approved: true });
      toast.success("Visto Bueno confirmado");
      onRefresh();
      const r = await axios.get(`${API}/adjudicaciones/${adj.id}/arranque`);
      setPlan(r.data);
    } catch { toast.error("Error"); }
  };

  const estadoColor = { pendiente: "bg-gray-100 text-gray-500", en_curso: "bg-blue-100 text-blue-700", completado: "bg-green-100 text-green-700" };

  const updateFase = (idx, key, val) => setForm(f => {
    const fases = [...f.fases]; fases[idx] = { ...fases[idx], [key]: val }; return { ...f, fases };
  });

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft size={14} /> Volver a adjudicaciones
      </button>

      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{adj.title}</h2>
            <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5"><Building2 size={12} /> {adj.municipality}</p>
          </div>
          <div className="flex items-center gap-2">
            {plan?.visto_bueno ? (
              <span className="flex items-center gap-1.5 bg-green-100 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                <CheckCircle size={12} /> VB Aprobado por {plan.visto_bueno_by}
              </span>
            ) : (
              plan && !editing && (
                <button onClick={vistoBueno} className="flex items-center gap-1.5 bg-[#007934] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#006528]">
                  <CheckCircle size={12} /> Dar Visto Bueno
                </button>
              )
            )}
            {!editing ? (
              <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 border border-gray-200 text-gray-600 text-xs px-3 py-2 rounded-lg hover:bg-gray-50">
                <Edit2 size={12} /> {plan ? "Editar plan" : "Crear plan"}
              </button>
            ) : (
              <button onClick={save} disabled={saving} className="flex items-center gap-1.5 bg-[#007934] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#006528] disabled:opacity-50">
                {saving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />} Guardar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Jefe de servicio */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">Jefe de Servicio</label>
        {editing ? (
          <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] max-w-sm"
            value={form.jefe_servicio} onChange={e => setForm(f => ({ ...f, jefe_servicio: e.target.value }))} placeholder="Nombre del jefe de servicio" />
        ) : (
          <p className="text-sm font-medium text-gray-800">{plan?.jefe_servicio || "—"}</p>
        )}
      </div>

      {/* Fases */}
      <div className="space-y-3">
        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Plan de ejecución por meses</h3>
        {(editing ? form.fases : (plan?.fases || DEFAULT_FASES)).map((fase, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className={`flex items-center gap-3 px-4 py-3 ${fase.estado === "completado" ? "bg-green-50" : fase.estado === "en_curso" ? "bg-blue-50" : "bg-gray-50"}`}>
              <div className="w-7 h-7 rounded-full bg-[#007934] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                {fase.mes}
              </div>
              <div className="flex-1">
                {editing ? (
                  <input className="w-full font-semibold text-sm text-gray-900 bg-transparent border-b border-gray-200 focus:outline-none focus:border-[#007934]"
                    value={fase.titulo} onChange={e => updateFase(i, "titulo", e.target.value)} />
                ) : (
                  <p className="font-semibold text-sm text-gray-900">Mes {fase.mes} — {fase.titulo}</p>
                )}
              </div>
              {editing ? (
                <select className="text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none"
                  value={fase.estado} onChange={e => updateFase(i, "estado", e.target.value)}>
                  <option value="pendiente">Pendiente</option>
                  <option value="en_curso">En curso</option>
                  <option value="completado">Completado</option>
                </select>
              ) : (
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${estadoColor[fase.estado] || estadoColor.pendiente}`}>
                  {fase.estado}
                </span>
              )}
            </div>
            <div className="px-4 py-3">
              {editing ? (
                <textarea rows={2} className="w-full text-xs text-gray-600 bg-transparent border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-[#007934] resize-none"
                  value={fase.descripcion} onChange={e => updateFase(i, "descripcion", e.target.value)} placeholder="Descripción de la fase..." />
              ) : (
                <p className="text-xs text-gray-600">{fase.descripcion}</p>
              )}
              {editing && (
                <div className="mt-2">
                  <label className="text-[10px] text-gray-400 uppercase font-semibold">Responsable</label>
                  <input className="w-full mt-0.5 border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#007934]"
                    value={fase.responsable || ""} onChange={e => updateFase(i, "responsable", e.target.value)} placeholder="Nombre responsable de fase" />
                </div>
              )}
              {!editing && fase.responsable && (
                <p className="text-[11px] text-[#007934] mt-1 font-medium">Responsable: {fase.responsable}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Recursos */}
      {editing && (
        <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Recursos necesarios</h3>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Licencias (una por línea)</label>
            <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none"
              value={(form.recursos_licencias || []).join("\n")}
              onChange={e => setForm(f => ({ ...f, recursos_licencias: e.target.value.split("\n").filter(Boolean) }))}
              placeholder="SiGEUS Core&#10;Google Maps API&#10;..." />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Infraestructura (una por línea)</label>
            <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none"
              value={(form.recursos_infra || []).join("\n")}
              onChange={e => setForm(f => ({ ...f, recursos_infra: e.target.value.split("\n").filter(Boolean) }))}
              placeholder="Servidor cloud 4 vCPU / 16GB&#10;..." />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Notas adicionales</label>
            <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none"
              value={form.notas || ""} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} />
          </div>
        </div>
      )}
      {!editing && plan && (
        <div className="grid grid-cols-2 gap-4">
          {plan.recursos_licencias?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs font-bold text-gray-500 uppercase mb-2">Licencias</p>
              {plan.recursos_licencias.map((l, i) => <p key={i} className="text-sm text-gray-700">• {l}</p>)}
            </div>
          )}
          {plan.recursos_infra?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs font-bold text-gray-500 uppercase mb-2">Infraestructura</p>
              {plan.recursos_infra.map((l, i) => <p key={i} className="text-sm text-gray-700">• {l}</p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────
export default function Adjudicaciones() {
  const { user } = useAuth();
  const { dg, setDg, filterByDG } = useDGFilter(user?.email);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");

  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/adjudicaciones`); setList(r.data || []); }
    catch { toast.error("Error cargando adjudicaciones"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // iter134 · aplicar filtro DG global antes de filtrar por estado y KPIs
  const listF = filterByDG(list);
  const filtered = filterStatus === "all" ? listF : listF.filter(a => a.status === filterStatus);

  const kpis = {
    primero: listF.filter(a => a.status === "primero").length,
    para_firma: listF.filter(a => a.status === "para_firma").length,
    firmado: listF.filter(a => a.status === "firmado").length,
    arrancado: listF.filter(a => a.status === "arrancado" || a.status === "en_ejecucion").length,
    importe: listF.reduce((s, a) => s + (a.contrato_importe || 0), 0),
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta adjudicación?")) return;
    await axios.delete(`${API}/adjudicaciones/${id}`);
    toast.success("Eliminada");
    load();
  };

  if (selected) return <div className="p-6"><PlanArranqueView adj={selected} onBack={() => setSelected(null)} onRefresh={load} /></div>;

  return (
    <div className="p-6 space-y-6 w-full">
      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Adjudicaciones</h1>
          <p className="text-sm text-gray-500 mt-0.5">Contratos ganados, para firma y en ejecución · sincronizados automáticamente desde Contratación VSM</p>
        </div>
        <div className="flex items-center gap-2">
          <DGFilter value={dg} onChange={setDg} size="sm" testidPrefix="adj-dg-filter"/>
          <SyncFromVsmButton onSynced={load}/>
          <button onClick={() => setShowCreate(true)} data-testid="create-adjudicacion-btn"
            className="flex items-center gap-2 bg-[#007934] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#006528] shadow-sm">
            <Plus size={16} /> Nueva adjudicación
          </button>
        </div>
      </div>

      {dg && (
        <div className="text-[11px] text-slate-500 -mt-3" data-testid="adj-dg-active-note">
          Filtro <b className="text-slate-700">{DG_LABELS[dg]}</b> · {listF.length} de {list.length} adjudicaciones
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "1º Clasificados", value: kpis.primero, icon: Trophy, color: "text-yellow-600" },
          { label: "Para Firma", value: kpis.para_firma, icon: FileSignature, color: "text-blue-600" },
          { label: "Firmados", value: kpis.firmado, icon: CheckCircle, color: "text-purple-600" },
          { label: "Arrancados", value: kpis.arrancado, icon: Rocket, color: "text-green-600",
            help: "Refleja únicamente lo marcado a mano en este contrato (nadie lo cambia automáticamente al firmar). Un contrato recién firmado NO cuenta aquí aunque su implantación ya haya empezado — mira la columna \"Implantación\" de la tabla para el progreso real del traspaso." },
          { label: "Importe Total", value: kpis.importe > 0 ? `${(kpis.importe/1000000).toFixed(1)}M€` : "—", icon: Euro, color: "text-[#007934]" },
        ].map(({ label, value, icon: Icon, color, help }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3" data-testid={`adj-kpi-${label}`}>
            <Icon size={18} className={color} />
            <div>
              {loading ? (
                <div className="h-6 w-8 bg-gray-100 rounded animate-pulse" data-testid="adj-kpi-loading"/>
              ) : (
                <p className={`text-xl font-bold ${color}`}>{value}</p>
              )}
              <p className="text-xs text-gray-400 flex items-center gap-1">{label}{help && <HelpTip text={help} />}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilterStatus("all")} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${filterStatus === "all" ? "bg-[#007934] text-white border-[#007934]" : "border-gray-200 text-gray-500 hover:border-[#007934]"}`}>
          Todos ({loading ? "…" : list.length})
        </button>
        {STATUSES.map(s => (
          <button key={s.value} onClick={() => setFilterStatus(s.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${filterStatus === s.value ? "bg-[#007934] text-white border-[#007934]" : "border-gray-200 text-gray-500 hover:border-[#007934]"}`}>
            {s.label} ({loading ? "…" : list.filter(a => a.status === s.value).length})
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-[#007934]" size={24} /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-16 text-center">
          <Trophy size={36} className="mx-auto mb-3 text-gray-200" />
          <p className="font-semibold text-gray-400">Sin adjudicaciones registradas</p>
          <p className="text-sm text-gray-400 mt-1">Añade un contrato ganado para empezar</p>
          <button onClick={() => setShowCreate(true)} className="mt-4 bg-[#007934] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#006528]">
            + Nueva adjudicación
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>{["Contrato", "Municipio", "Estado", "Implantación", "Importe", "Firma prevista", "Resp. Técnico", "Servicios TIC", ""].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(a => {
                const sc = STATUS_CONFIG[a.status] || STATUS_CONFIG.primero;
                return (
                  <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900 text-sm max-w-[200px]">{a.title}</td>
                    <td className="px-4 py-3 text-sm text-gray-600"><span className="flex items-center gap-1"><Building2 size={11} />{a.municipality}</span></td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${sc.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />{sc.label}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-testid={`adj-implantacion-${a.id}`}>
                      {a.implantacion ? (
                        <a href={`/checklists?open=${a.implantacion.checklist_id}`} className="inline-flex flex-col gap-0.5 hover:underline" title={a.implantacion.match_basis === "municipio" ? "Cruce por municipio (sin expediente exacto vinculado)" : "Cruce por expediente exacto"}>
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full w-fit ${HANDOFF_COLOR[a.implantacion.handoff_estado] || HANDOFF_COLOR.borrador}`}>
                            {HANDOFF_LABELS[a.implantacion.handoff_estado] || a.implantacion.handoff_estado}
                          </span>
                          <span className="text-[10px] text-gray-400">
                            {a.implantacion.progress_pct == null ? "N/D" : `${a.implantacion.progress_pct}%`}
                            {a.implantacion.match_basis === "municipio" && " · aprox."}
                          </span>
                        </a>
                      ) : (
                        <span className="text-[10px] text-gray-300">Sin checklist vinculado</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 font-medium">{a.contrato_importe ? `${a.contrato_importe.toLocaleString("es-ES")}€` : "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{a.fecha_firma_prevista ? a.fecha_firma_prevista.substring(0, 10) : "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{a.responsible_tech || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(a.servicios || []).slice(0, 3).map(s => (
                          <span key={s} className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{s}</span>
                        ))}
                        {(a.servicios || []).length > 3 && <span className="text-[10px] text-gray-400">+{a.servicios.length - 3}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSelected(a)} className="flex items-center gap-1 text-xs text-[#007934] font-medium hover:underline" data-testid={`plan-btn-${a.id}`}>
                          <ClipboardList size={12} /> Plan
                        </button>
                        <button onClick={() => del(a.id)} className="ml-2 text-red-400 hover:text-red-600"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
